/**
 * CustomerHero.jsx — Iter 168 (Danny)
 *
 * Mirrors the Lead Focus hero exactly, but reshaped for Customers:
 *   - Same back button + logo + name + chip strip + action buttons
 *   - Stage strip = IMPLEMENTATION TRACK stages (EHR/PMS 7 or
 *     Care Coord 6 — Hybrid customers see BOTH tracks stacked).
 *     Live customers see a single "✓ Live customer" badge.
 *   - KPI strip = customer-centric (MRR, Enrolled products,
 *     Patient count, Days since kickoff, Estimated go-live).
 *
 * Backend dependency:
 *   - GET /api/customers/{id}/bucket  -> bucket, tracks, implementation, enrolled_products, days_since_kickoff
 *   - GET /api/contacts/{id}/enrollments -> active_count, active_total_monthly, active_total_patients
 */
import React, { useState } from "react";
import { ArrowLeft, ChevronDown, Sparkles, FileText, Phone, Mail, MessageSquare, Calendar, ArrowUpRight, CheckCircle2, TrendingUp, AlertOctagon, Archive } from "lucide-react";

const EHR_PMS_STAGES = [
  { id: 1, label: "Kickoff" },
  { id: 2, label: "Data Collection" },
  { id: 3, label: "System Build" },
  { id: 4, label: "Data Migration" },
  { id: 5, label: "Training" },
  { id: 6, label: "Go-Live" },
  { id: 7, label: "Post Go-Live" },
];

const CARE_COORD_STAGES = [
  { id: 1, label: "Patient roster" },
  { id: 2, label: "Patient consent" },
  { id: 3, label: "Onboarding calls" },
  { id: 4, label: "Care plan docs" },
  { id: 5, label: "First billing" },
  { id: 6, label: "Steady-state" },
];

const initials = (name) => (name || "").split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();

const fmtCompactCurrency = (n) => {
  if (n == null) return "—";
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${Number(n).toLocaleString()}`;
};
const fmtNum = (n) => (n == null ? "—" : Number(n).toLocaleString());
const fmtDateShort = (iso) => { if (!iso) return "—"; try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); } catch { return iso; } };

function Kpi({ label, value, unit, trend, trendUp, testid }) {
  return (
    <div data-testid={testid || `customer-kpi-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="eyebrow" style={{ marginBottom: 6 }}>{label}</div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
        <span className="num" style={{ fontSize: 28, fontWeight: 600, color: "var(--navy)", letterSpacing: "-0.015em", lineHeight: 1.1 }}>{value}</span>
        {unit && <span className="num" style={{ fontSize: 12, color: "var(--text-3)" }}>{unit}</span>}
      </div>
      <div style={{ fontSize: 11, color: trendUp ? "var(--success)" : "var(--text-3)", marginTop: 6, display: "inline-flex", alignItems: "center", gap: 3 }}>
        {trendUp && <TrendingUp style={{ width: 10, height: 10 }} />}
        {trend}
      </div>
    </div>
  );
}

function CommsPopover({ contact, onSelect, onClose }) {
  const items = [
    { id: "call", icon: Phone, color: "var(--success)", label: "Call", sub: contact?.phone || "—" },
    { id: "email", icon: Mail, color: "var(--blue)", label: "Email", sub: contact?.email || "—" },
    { id: "sms", icon: MessageSquare, color: "#7c3aed", label: "SMS", sub: "Send a text message" },
    { id: "fax", icon: FileText, color: "#0891b2", label: "Log a fax", sub: "Manual log — fax via RC outside CRM" },
    { id: "meeting", icon: Calendar, color: "var(--blue)", label: "Schedule meeting", sub: "Onboarding / check-in / QBR" },
    { id: "quote", icon: FileText, color: "var(--navy)", label: "Send upsell quote", sub: "Generate an expansion quote", primary: true },
  ];
  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 80 }} />
      <div data-testid="customer-comms-popover" style={{ position: "absolute", top: "calc(100% + 8px)", right: 0, width: 280, background: "var(--paper)", border: "0.5px solid var(--border)", borderRadius: 12, boxShadow: "var(--shadow-float)", zIndex: 81, overflow: "hidden" }}>
        <div style={{ padding: "10px 14px 8px", borderBottom: "0.5px solid var(--border-soft)" }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: "var(--navy)" }}>Outreach</div>
          <div style={{ fontSize: 11, color: "var(--text-3)" }}>to {[contact?.first_name, contact?.last_name].filter(Boolean).join(" ")}</div>
        </div>
        {items.map((i) => {
          const Icon = i.icon;
          return (
            <button key={i.id} data-testid={`customer-comms-item-${i.id}`} onClick={() => onSelect(i.id)}
              style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", width: "100%", border: "none", background: i.primary ? "var(--blue-soft)" : "transparent", cursor: "pointer", textAlign: "left", fontFamily: "inherit" }}>
              <Icon style={{ width: 16, height: 16, color: i.color, strokeWidth: 2 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: i.primary ? "var(--blue)" : "var(--text)" }}>{i.label}</div>
                <div className="num" style={{ fontSize: 11, color: "var(--text-3)" }}>{i.sub}</div>
              </div>
              {i.primary && <ArrowUpRight style={{ width: 12, height: 12, color: "var(--blue)" }} />}
            </button>
          );
        })}
      </div>
    </>
  );
}

/**
 * StageStrip — renders a horizontal pill row for one implementation
 * track. Mirrors the visual treatment of LeadFocus.HeroBlock stage
 * pills (done → soft green, current → solid blue, future → dashed).
 */
function StageStrip({ label, stages, currentStage, onAdvance, canEdit, testid }) {
  const isDone = currentStage >= stages.length;
  return (
    <div data-testid={testid} style={{ marginTop: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: "var(--navy)", textTransform: "uppercase", letterSpacing: "0.04em" }}>{label}</span>
        <span style={{ fontSize: 11, color: isDone ? "var(--success)" : "var(--text-3)", fontWeight: 500 }}>
          {isDone ? "✓ Live" : `Stage ${currentStage || 0} of ${stages.length}`}
        </span>
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {stages.map((s) => {
          const done = s.id < currentStage;
          const current = s.id === currentStage;
          let bg = "transparent", color = "var(--text-3)", border = "0.5px dashed var(--border)";
          if (done) { bg = "var(--success-soft)"; color = "#065f46"; border = "0.5px solid #bbf7d0"; }
          if (current) { bg = "var(--blue)"; color = "#fff"; border = "0.5px solid var(--blue)"; }
          return (
            <button
              key={s.id}
              data-testid={`${testid}-stage-${s.id}`}
              disabled={!canEdit}
              onClick={() => canEdit && onAdvance(s.id)}
              style={{
                display: "inline-flex", flexDirection: "column", alignItems: "flex-start",
                padding: "8px 14px", borderRadius: 12, fontSize: 12, fontWeight: 500,
                background: bg, color, border, fontFamily: "inherit",
                cursor: canEdit ? "pointer" : "default",
                transition: "background-color 120ms ease, color 120ms ease",
                minWidth: 92, textAlign: "left",
              }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                {(done || current) && <span style={{ width: 5, height: 5, borderRadius: "50%", background: current ? "#fff" : "var(--success)" }} />}
                {s.label}
              </span>
              <span style={{ fontSize: 10, opacity: 0.6, marginTop: 2 }}>{done ? "Completed" : current ? "Current" : "Upcoming"}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function CustomerHero({
  contact,
  bucketData,           // { bucket, tracks, implementation, enrolled_products, days_since_kickoff }
  enrollmentSummary,    // { active_count, active_total_monthly, active_total_patients }
  onBack,
  onComms,
  onAiAssist,
  onQuickNote,
  onAddProduct,
  onAdvanceStage,       // (trackKey, stageId) => void
  canEditImpl = false,
  // Iter 171.A — Churned / Exited account controls (Danny Phase A)
  onFlagForClosure,     // rep — flag for manager review
  onMoveToChurned,      // manager — opens the Exit Form modal
  onReEngage,           // rep/manager — creates re-engagement lead
  canManageChurn = false, // true for super_admin / sales_manager / crm_admin
}) {
  const [commsOpen, setCommsOpen] = useState(false);
  const name = `${contact?.first_name || ""} ${contact?.last_name || ""}`.trim() || contact?.company || "Customer";
  const isLive = bucketData?.bucket === "live";
  const isChurned = bucketData?.bucket === "churned_exited" || contact?.bucket === "churned_exited" || !!contact?.exit?.sub_type;
  const exitData = contact?.exit || {};
  // Iter 171.A — Friendly sub-type labels. The list endpoint decorator
  // (`_exit_sub_type_label`) provides this for the bucket list, but the
  // detail endpoint doesn't run through decorate(), so we fall back to
  // an inline label map here. Keep this in sync with backend
  // EXIT_SUB_TYPES in customers_routes.py.
  const EXIT_LABELS = {
    closed_shutdown:      "Closed — Practice shut down",
    closed_retired:       "Closed — Physician retired",
    sold_acquired:        "Sold — Practice acquired",
    churned_competitor:   "Churned — Switched to competitor",
    churned_non_payment:  "Churned — Non-payment / Collections",
    churned_dissatisfied: "Churned — Service dissatisfied",
    churned_unresponsive: "Churned — Unknown / Unresponsive",
  };
  const exitLabel = contact?._exit_sub_type_label
    || EXIT_LABELS[exitData.sub_type]
    || exitData.sub_type;
  const tracks = bucketData?.tracks || [];
  const impl = bucketData?.implementation || {};
  const ehrStage = impl.ehr_pms_stage || 0;
  const ccStage = impl.care_coord_stage || 0;

  const mrr = enrollmentSummary?.active_total_monthly || 0;
  const patientCount = enrollmentSummary?.active_total_patients || contact?.num_patients || 0;
  const productCount = enrollmentSummary?.active_count || (bucketData?.enrolled_products || []).length;
  const daysSinceKickoff = bucketData?.days_since_kickoff;
  const eta = impl?.estimated_go_live;

  // Surface chips below the name (mirrors the lead-focus "chip strip")
  const chips = isChurned
    ? [
        { dot: "#64748B", text: exitLabel || "Churned / Exited", testid: "customer-chip-bucket" },
        ...(exitData.date ? [{ dot: "#64748B", text: `Exit ${fmtDateShort(exitData.date)}`, testid: "customer-chip-exit-date" }] : []),
        ...(exitData.revenue_at_exit ? [{ dot: "#64748B", text: `Revenue at exit ${fmtCompactCurrency(exitData.revenue_at_exit)}`, testid: "customer-chip-revenue-at-exit" }] : []),
      ]
    : [
        { dot: isLive ? "var(--success)" : "var(--blue)", text: isLive ? "Live customer" : "In implementation", testid: "customer-chip-bucket" },
        ...(contact?.specialty ? [{ dot: "var(--blue)", text: contact.specialty, testid: "customer-chip-specialty" }] : []),
        ...(productCount > 0 ? [{ dot: "var(--success)", text: `${productCount} active product${productCount === 1 ? "" : "s"}` }] : []),
        ...(daysSinceKickoff != null && !isLive ? [{ dot: "var(--text-4)", text: `${daysSinceKickoff}d since kickoff` }] : []),
        ...(impl.specialist_user_id ? [{ dot: "var(--blue)", text: "Specialist assigned" }] : []),
      ];

  return (
    <div className="card" style={{ padding: "26px 30px", borderRadius: "var(--radius-lg)" }}>
      <button
        data-testid="customer-focus-back"
        onClick={onBack}
        style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          padding: "5px 10px", borderRadius: 8,
          background: "transparent", border: "0.5px solid var(--border)",
          color: "var(--text-3)", fontSize: 11, fontWeight: 500,
          cursor: "pointer", fontFamily: "inherit", marginBottom: 16,
        }}>
        <ArrowLeft style={{ width: 12, height: 12 }} /> Back to customers
      </button>

      <div style={{ display: "flex", gap: 18, alignItems: "flex-start" }}>
        <div style={{ width: 64, height: 64, flexShrink: 0, background: isChurned ? "#64748B" : (isLive ? "var(--success)" : "var(--navy)"), borderRadius: 16, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "var(--font-head)", fontSize: 24, fontWeight: 700 }}>
          {initials(contact?.company || name)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1 style={{ fontSize: 30, fontWeight: 700, color: "var(--navy)", letterSpacing: "-0.025em", marginBottom: 4, lineHeight: 1.15 }} data-testid="customer-name">
            {contact?.company || name}
          </h1>
          <div style={{ fontSize: 13, color: "var(--text-3)", lineHeight: 1.5 }}>
            {name && <span style={{ fontWeight: 600, color: "var(--text-2)" }}>{name}</span>}
            {contact?.job_title && <><span style={{ margin: "0 8px", color: "var(--text-4)" }}>·</span><span>{contact.job_title}</span></>}
            {contact?.specialty && <><span style={{ margin: "0 8px", color: "var(--text-4)" }}>·</span><span>{contact.specialty}</span></>}
            {contact?.email && <><span style={{ margin: "0 8px", color: "var(--text-4)" }}>·</span><span className="num" style={{ fontSize: 12 }}>{contact.email}</span></>}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 12 }}>
            {chips.map((c, i) => (
              <span key={i} data-testid={c.testid} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 11px", borderRadius: 999, fontSize: 11, fontWeight: 500, background: "var(--paper)", color: "var(--text-2)", border: "0.5px solid var(--border)" }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: c.dot }} />
                {c.text}
              </span>
            ))}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, position: "relative", flexWrap: "wrap", justifyContent: "flex-end" }}>
          <button data-testid="customer-comms-btn" onClick={() => setCommsOpen((v) => !v)} className="btn btn-primary" style={{ paddingRight: 10 }}>
            Communications <ChevronDown style={{ width: 12, height: 12 }} />
          </button>
          <button data-testid="customer-ai-assist-btn" onClick={onAiAssist} className="btn btn-accent">
            <Sparkles style={{ width: 13, height: 13 }} /> AI assist
          </button>
          <button
            data-testid="customer-add-product-btn"
            onClick={onAddProduct}
            className="btn"
            style={{ background: "var(--blue-soft)", color: "var(--blue)", border: "0.5px solid #cfe2ff" }}
            title="Add a product enrollment — moves the customer to Implementation if pending">
            <ArrowUpRight style={{ width: 13, height: 13 }} /> Add product
          </button>
          <button
            data-testid="customer-quick-note-btn"
            onClick={onQuickNote}
            className="btn"
            style={{ background: "#FFF8E5", color: "#92400E", border: "0.5px solid #FCD34D" }}>
            <FileText style={{ width: 13, height: 13 }} /> Quick note
          </button>
          {/* Iter 171.A — Churn-related action buttons (Danny Phase A). */}
          {!isChurned && onFlagForClosure && (
            <button
              data-testid="customer-flag-for-closure-btn"
              onClick={onFlagForClosure}
              className="btn"
              title="Notify the sales manager to review and move this customer to Churned / Exited"
              style={{ background: "#FEF2F2", color: "#991B1B", border: "0.5px solid #FCA5A5" }}>
              <AlertOctagon style={{ width: 13, height: 13 }} /> Flag for closure
            </button>
          )}
          {!isChurned && canManageChurn && onMoveToChurned && (
            <button
              data-testid="customer-move-to-churned-btn"
              onClick={onMoveToChurned}
              className="btn"
              title="Manager-only — open the exit form to move this customer to Churned / Exited"
              style={{ background: "#F1F5F9", color: "#334155", border: "0.5px solid #CBD5E1" }}>
              <Archive style={{ width: 13, height: 13 }} /> Move to Churned
            </button>
          )}
          {isChurned && onReEngage && contact?._re_engage_eligible && (
            <button
              data-testid="customer-re-engage-btn"
              onClick={onReEngage}
              className="btn"
              title="Create a re-engagement lead linked back to this churned customer"
              style={{ background: "var(--blue)", color: "#fff" }}>
              <ArrowUpRight style={{ width: 13, height: 13 }} /> Re-engage
            </button>
          )}
          {commsOpen && <CommsPopover contact={contact} onSelect={(action) => { setCommsOpen(false); onComms?.(action); }} onClose={() => setCommsOpen(false)} />}
        </div>
      </div>

      {/* Iter 171.A — Churned / Exited banner replaces the Live/Impl strip. */}
      {isChurned ? (
        <div data-testid="customer-churned-banner" style={{ marginTop: 22, padding: "12px 16px", borderRadius: 12, background: "#F1F5F9", border: "0.5px solid #CBD5E1", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Archive style={{ width: 16, height: 16, color: "#64748B" }} />
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#334155" }}>
                {exitLabel || "Churned / Exited"}
                {exitData.date && <span style={{ color: "#64748B", fontWeight: 500 }}> · {fmtDateShort(exitData.date)}</span>}
              </div>
              {exitData.notes && (
                <div style={{ fontSize: 11, color: "#475569", marginTop: 3, maxWidth: 600 }} data-testid="customer-churned-notes">
                  {exitData.notes}
                </div>
              )}
            </div>
          </div>
          {contact?._re_engage_eligible ? (
            <span data-testid="customer-re-engage-eligible-pill" style={{ fontSize: 10, fontWeight: 700, padding: "3px 9px", borderRadius: 12, background: "var(--blue-light)", color: "var(--blue)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
              Re-engagement eligible
            </span>
          ) : null}
        </div>
      ) : isLive && tracks.length === 0 ? (
        <div data-testid="customer-live-badge" style={{ marginTop: 22, padding: "12px 16px", borderRadius: 12, background: "var(--success-soft)", border: "0.5px solid #bbf7d0", display: "inline-flex", alignItems: "center", gap: 8 }}>
          <CheckCircle2 style={{ width: 16, height: 16, color: "var(--success)" }} />
          <span style={{ fontSize: 13, fontWeight: 600, color: "#065f46" }}>Live customer — all products in steady state</span>
        </div>
      ) : tracks.length === 0 ? (
        <div data-testid="customer-no-tracks-banner" style={{ marginTop: 22, padding: "12px 16px", borderRadius: 12, background: "var(--blue-soft)", border: "0.5px solid #cfe2ff", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
          <span style={{ fontSize: 12, color: "var(--text-2)" }}>
            No implementation track assigned yet.
            {isLive ? " Customer is Live but has no products — add one to start the tracker." : " Add a product enrollment below to activate the tracker."}
          </span>
          {onAddProduct && (
            <button
              data-testid="customer-no-tracks-add-product"
              onClick={onAddProduct}
              className="btn"
              style={{ background: "var(--blue)", color: "#fff", fontSize: 11, padding: "5px 12px" }}>
              <ArrowUpRight style={{ width: 11, height: 11 }} /> Add product
            </button>
          )}
        </div>
      ) : (
        <>
          {tracks.includes("ehr_pms") && (
            <StageStrip label="EHR / PMS Implementation" stages={EHR_PMS_STAGES} currentStage={ehrStage} canEdit={canEditImpl}
              onAdvance={(stageId) => onAdvanceStage?.("ehr_pms_stage", stageId)} testid="customer-stage-strip-ehr-pms" />
          )}
          {tracks.includes("care_coord") && (
            <StageStrip label="Care Coordination" stages={CARE_COORD_STAGES} currentStage={ccStage} canEdit={canEditImpl}
              onAdvance={(stageId) => onAdvanceStage?.("care_coord_stage", stageId)} testid="customer-stage-strip-care-coord" />
          )}
        </>
      )}

      {/* KPI strip — 5 customer-centric metrics (mirrors lead-focus layout) */}
      <div data-testid="customer-kpi-strip" style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 18, marginTop: 22, paddingTop: 18, borderTop: "0.5px solid var(--border-soft)" }}>
        <Kpi label="MRR" value={fmtCompactCurrency(mrr)} unit={mrr ? "/mo" : ""} trend={mrr ? "Active products" : "No active products"} trendUp={mrr > 0} />
        <Kpi label="Products" value={`${productCount}`} unit={productCount === 1 ? "enrolled" : "enrolled"} trend={isLive ? "All live" : tracks.length ? `${tracks.length} track${tracks.length === 1 ? "" : "s"}` : "—"} trendUp={isLive} />
        <Kpi label="Patient count" value={fmtNum(patientCount)} unit={patientCount ? "patients" : ""} trend={patientCount ? "Enrolled" : "Not yet"} trendUp={patientCount > 0} />
        <Kpi label="Days since kickoff" value={daysSinceKickoff != null ? `${daysSinceKickoff}` : "—"} unit={daysSinceKickoff != null ? "days" : ""} trend={impl?.kickoff_at ? `Kicked off ${fmtDateShort(impl.kickoff_at)}` : "No kickoff date"} />
        <Kpi label="Est. go-live" value={eta ? fmtDateShort(eta) : "—"} trend={isLive ? "Already live" : eta ? "Scheduled" : "Not set"} trendUp={isLive} />
      </div>
    </div>
  );
}
